/**
 * Loads, decrypts, and validates an artifact envelope.
 *
 * This module is the single entry point for all artifact I/O in the viewer.
 * It is used by App.tsx and the viewer tests.
 *
 * M5 change: loadArtifact now accepts either a passphrase OR a pre-derived
 * key (Uint8Array). The pre-derived key path is used by the passkey unlock
 * flow, which unwraps the session key from IndexedDB without needing the
 * passphrase again.
 */

import {
  ArtifactEnvelopeSchema,
  parseArtifactBody,
  SCHEMA_VERSION,
  type ArtifactBody,
  type ArtifactEnvelope,
} from '@pf/contract';
import { deriveKey, open } from '@pf/crypto-shared';

// ---------------------------------------------------------------------------
// Schema version compatibility
// ---------------------------------------------------------------------------

/**
 * Parses the major version number from a semver string.
 * Returns NaN if the string is not valid semver.
 */
function majorVersion(v: string): number {
  const match = /^(\d+)\./.exec(v);
  return match?.[1] !== undefined ? parseInt(match[1], 10) : NaN;
}

/**
 * Checks whether an envelope's schemaVersion is compatible with the viewer.
 *  - Same major → compatible (viewer is forward-compatible with additive changes).
 *  - Different major → incompatible (breaking change, refuse with clear message).
 */
export function checkSchemaVersion(envelopeVersion: string): void {
  const currentMajor = majorVersion(SCHEMA_VERSION);
  const envelopeMajor = majorVersion(envelopeVersion);

  if (isNaN(envelopeMajor)) {
    throw new Error(
      `Invalid schema version in artifact: "${envelopeVersion}". ` +
        `Expected semver format (e.g. "1.0.0").`,
    );
  }

  if (envelopeMajor !== currentMajor) {
    throw new Error(
      `Schema version mismatch: artifact uses version "${envelopeVersion}", ` +
        `but this viewer only supports major version ${currentMajor} (${SCHEMA_VERSION}). ` +
        `Please update the viewer to open this artifact.`,
    );
  }
}

// ---------------------------------------------------------------------------
// Main load function
// ---------------------------------------------------------------------------

export interface LoadArtifactResult {
  /** The validated artifact body. */
  body: ArtifactBody;
  /** The parsed envelope. */
  envelope: ArtifactEnvelope;
  /**
   * The 32-byte AES-256-GCM key used to decrypt the artifact.
   * Callers should hold this in memory only for the duration of the session
   * and discard it on lock.
   */
  key: Uint8Array;
}

/**
 * Accepts either a passphrase (string) or a pre-derived key (Uint8Array).
 *
 * - Passphrase path: derives the key from the passphrase + KDF params in the
 *   envelope. Used for the canonical passphrase unlock flow.
 * - Pre-derived key path: uses the supplied key directly. Used by the passkey
 *   unlock flow, which has already unwrapped the session key from IndexedDB.
 *   The key must be exactly 32 bytes (AES-256).
 */
export type UnlockCredential =
  | { kind: 'passphrase'; passphrase: string }
  | { kind: 'key'; key: Uint8Array };

/**
 * Validates the envelope schema, checks the schema version, resolves the
 * decryption key (from passphrase or pre-derived), decrypts the ciphertext,
 * and parses the body.
 *
 * Throws descriptive errors for:
 *  - Invalid envelope JSON shape
 *  - Incompatible schema version (major mismatch)
 *  - Wrong passphrase / tampered ciphertext (AES-GCM auth failure)
 *  - Tampered ciphertext without wrong key (GCM auth failure with clear message)
 *  - Body validation failure (Zod)
 *
 * NOTE: The equality check for the passphrase path is performed entirely by
 * the GCM authentication tag. A wrong passphrase derives a different key,
 * which causes gcm.decrypt() to throw. There is no string comparison of
 * keys or passphrases.
 */
export async function loadArtifact(
  rawEnvelope: unknown,
  credential: UnlockCredential | string,
): Promise<LoadArtifactResult> {
  // Normalise legacy string call-sites (passphrase string directly)
  const cred: UnlockCredential =
    typeof credential === 'string'
      ? { kind: 'passphrase', passphrase: credential }
      : credential;

  // 1. Validate envelope shape
  const envelope = ArtifactEnvelopeSchema.parse(rawEnvelope);

  // 2. Check schema version compatibility
  checkSchemaVersion(envelope.schemaVersion);

  // 3. Resolve decryption key
  let key: Uint8Array;
  if (cred.kind === 'passphrase') {
    const salt = base64ToUint8Array(envelope.kdf.salt);
    key = await deriveKey(cred.passphrase, salt, {
      iterations: envelope.kdf.iterations,
      memoryKiB: envelope.kdf.memoryKiB,
      parallelism: envelope.kdf.parallelism,
    });
  } else {
    key = cred.key;
  }

  // 4. Decrypt — wrong key or tampered ciphertext → GCM auth failure
  const iv = base64ToUint8Array(envelope.aead.iv);
  const ciphertext = base64ToUint8Array(envelope.ciphertext);

  let plaintext: Uint8Array;
  try {
    plaintext = open(ciphertext, key, iv);
  } catch (err) {
    if (cred.kind === 'passphrase') {
      throw new Error(
        'Decryption failed. Please check your passphrase.',
        { cause: err },
      );
    } else {
      // Pre-derived key path — ciphertext tamper or credential mismatch
      throw new Error(
        'Decryption failed: this file may have been altered, or the stored ' +
          'key no longer matches the artifact. Please unlock with your passphrase.',
        { cause: err },
      );
    }
  }

  // 5. Parse and validate body
  const bodyStr = new TextDecoder().decode(plaintext);
  const body = parseArtifactBody(JSON.parse(bodyStr) as unknown);

  return { body, envelope, key };
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
